/* eslint-disable @typescript-eslint/no-explicit-any */
/* istanbul ignore file */
import type { ChangeEvent } from 'react';
import React, { useRef, useState } from 'react';
import { IconInfo, InputField, PageSlider } from '@noths/polaris-client-ribbons-design-system';
import { Button } from '@noths/polaris-design-system-core-components';

import { ApplyToSellWithUsContainer } from 'src/components/molecules/ApplyToSellWithUsContainer/ApplyToSellWithUsContainer';
import { submitPartnerEmail } from 'src/services/post-form-data';
import * as styles from 'src/styles/styles';
import { logError } from 'src/utils/logError';
import * as COPY from './copy';

const isValidEmailAddress = (email: string) => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return regex.test(String(email).toLowerCase());
};

export const RegisterInterest = () => {
  // common between both forms
  const [errorState, setErrorState] = useState({});
  const [email, setEmail] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const setError = (errorName: string, errorMessage?: string) =>
    setErrorState((prevState) => ({ ...prevState, [errorName]: errorMessage }));

  const clearError = (errorName: string) => setError(errorName);

  const paginate = (direction: number) => () => {
    window.scrollTo({ top: 0 });
    setPageIndex(pageIndex + direction);
    return true;
  };

  const onFormChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);
  };

  // specific to this form
  const pageLast = () => {
    if (formRef.current) {
      paginate(formRef.current.children[0].children[0].childElementCount - 1)();
    }
  };

  const validateFormEmailOnly = (): boolean => {
    clearError('partner-email-error');
    const emailTrimmed = email.trim();
    if (!emailTrimmed) {
      setError('partner-email-error', 'Email address is required');
      return false;
    }
    if (!isValidEmailAddress(emailTrimmed)) {
      setError('partner-email-error', 'Email address is not valid');
      return false;
    }
    return true;
  };

  const handleSubmitEmailOnly = (e: any) => {
    e.preventDefault();

    if (validateFormEmailOnly()) {
      submitPartnerEmail(email)
        .then(() => {
          pageLast();
        })
        .catch((err) => {
          // no error page yet
          logError('Form submit error:', err);
          pageLast();
        });
    }
  };

  return (
    <ApplyToSellWithUsContainer>
      <form
        data-dd-privacy="mask"
        data-testid="registration-form"
        onSubmit={(e) => e.preventDefault()}
        ref={formRef}
      >
        <PageSlider pageIndex={pageIndex} transition="transform 400ms">
          <section css={styles.pageSection}>
            <div css={styles.warningField}>
              <IconInfo color="var(--brand-bluple-base)" css={styles.warningIcon} />
              <p>{COPY.APPLICATIONS_PAUSED_MESSAGE}</p>
            </div>
            <div css={styles.subSection}>
              <InputField
                css={styles.textField}
                error={errorState['partner-email-error']}
                id="partner-email-0"
                label="Your email address"
                name="email"
                onBlur={validateFormEmailOnly}
                onChange={onFormChange}
                type="email"
                value={email}
              />
            </div>
            <div css={styles.navigationButtonsGrid}>
              <Button
                disabled={!email || errorState['partner-email-error']}
                onClick={handleSubmitEmailOnly}
              >
                Submit
              </Button>
            </div>
          </section>
          <section css={styles.pageSection}>
            <>
              <p css={styles.finalSectionText}>{COPY.REGISTERED_INTEREST_SUCCESSFULLY}</p>
              <p css={styles.finalSectionText}>{COPY.WE_WILL_BE_IN_TOUCH}</p>
            </>
          </section>
        </PageSlider>
      </form>
    </ApplyToSellWithUsContainer>
  );
};
